import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { Fabric, WorkOrder } from '@app/models/order/work-order';
import { Tags } from '@app/models/tags/tags';
import { FabricProxyService } from '@app/proxies/item/fabric.proxy.service';
import { TagsProxyService } from '@app/proxies/item/tags.proxy.service';
import { DyeingForecastService } from '@app/services/dyeing-forecast.service';
import { EnumService } from '@app/services/enum.service';
import { ProductionCardFormGroup } from '@app/models/production/production-card-forms';
import { ProductionCardService } from '@app/services/production-card.service';
import { ProductionCardStatusUtil } from '@app/utilities/production-card-status.util';
import { LocaleHelperService } from '@app/services/locale-helper.service';
import { distinctUntilChanged, debounceTime } from 'rxjs';

@Component({
  selector: 'app-production-cards-viewer',
  templateUrl: './production-cards-viewer.component.html',
  styleUrl: './production-cards-viewer.component.scss',
})
export class ProductionCardsViewerComponent implements OnInit {
  @Input() readonly = false;
  @Input() currentProductionCards: FormArray<FormGroup<ProductionCardFormGroup>> | null = null;
  @Input() productionCardsSelected: FormArray<FormGroup<ProductionCardFormGroup>> = new FormArray<FormGroup<ProductionCardFormGroup>>([]);
  @Input() isForSelection = false;
  @Input() hasOverflow = false;
  @Input() showAssignRollButton = true;
  @Input() includeDeleted = false;

  // i need create output for emit production card is selected to knitting
  @Output() productionCardSelected = new EventEmitter<FormGroup<ProductionCardFormGroup>>();
  @Output() productionCardUnselected = new EventEmitter<FormGroup<ProductionCardFormGroup>>();
  public displayMode: 'grouped' | 'flat' = 'grouped';
  // eslint-disable-next-line @typescript-eslint/consistent-indexed-object-style
  expandedRows: { [key: string]: boolean } = {};
  filterStatusSelected: FormControl<number[] | null> = new FormControl(null);
  filterAdditionSelected: FormControl<number | null> = new FormControl(null);
  filterLoopLengthSelected: FormControl<number | null> = new FormControl(null);
  tags: Tags[] = [];
  filterAddition: { label: string; value: number }[] = [];
  public filterStatus: { label: string; value: number }[] = [];
  public filterLoopLength: number[] = [];
  allPCFabrics: Fabric[] = [];

  @Input() workOrder: WorkOrder | null = null;

  private productionCardService = inject(ProductionCardService);
  private dyeingForecastService = inject(DyeingForecastService);
  private itemProxyService = inject(FabricProxyService);
  private enumService = inject(EnumService);
  private tagsProxyService = inject(TagsProxyService);
  private router = inject(Router);
  private localeHelperService = inject(LocaleHelperService);

  constructor() {
    this.filterAddition = [
      { label: this.localeHelperService.translateWithFallback('productionCardViewer.firstAddition', 'First Addition'), value: 1 },
      { label: this.localeHelperService.translateWithFallback('productionCardViewer.lastAddition', 'Last Addition'), value: 2 },
    ];

    this.filterStatus = Object.values(this.enumService.getEnum('ProductionCardStatus', 'productionEnums')?.values || {}).map(
      (val, index) => ({
        label: val,
        value: index,
      }),
    );

    this.productionCardService.productionCardsFormArray.valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe(() => {
      this.itemProxyService.batchFabrics(this.productionCardService.getAllFabricsCodes()).subscribe((response) => {
        if (response.success && response.data) {
          this.allPCFabrics = response.data;
          this.getAllLoopLength();
        }
      });
    });
  }

  ngOnInit(): void {
    this.loadTags();
  }

  get productionCardsFormArray(): FormArray<FormGroup> {
    return this.productionCardService.productionCardsFormArray;
  }

  get filteredProductionCards() {
    const statusFilter = this.filterStatusSelected.value;
    const loopLengthMin = this.filterLoopLengthSelected.value;

    const cards = this.productionCardsFormArray.controls.filter((c) => {
      const status = c.getRawValue().status;

      // this is for filter production cards by status deleted
      if (!this.includeDeleted && status === 8) return false;

      // status filter
      const statusOk = !statusFilter?.length || statusFilter.includes(status);

      // loopLength filter
      let loopLengthOk = true;
      if (typeof loopLengthMin === 'number') {
        const entries = c.getRawValue().productionCardEntries || [];
        loopLengthOk = entries.some((entry: { fabricCode: string }) => {
          const loopLengths = this.getTotalLoopLengthByFabricCode(entry.fabricCode);
          return Array.isArray(loopLengths) && loopLengths.includes(loopLengthMin);
        });
      }

      return statusOk && loopLengthOk;
    });

    return this.filterAdditionSelected.value === 2 ? cards.slice().reverse() : cards;
  }

  productionCardTotalQty(productionCardFormGroup: FormGroup<ProductionCardFormGroup>) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return productionCardFormGroup.controls.productionCardEntries?.value.reduce((acc: number, entry: any) => {
      return acc + entry.quantity;
    }, 0);
  }

  public navigateToProductionCard(productionCardNumber: string) {
    this.router.navigate(['/production-card', productionCardNumber, 'details']);
  }

  get allAssets() {
    return this.dyeingForecastService.allAssets();
  }

  get allColors() {
    return this.dyeingForecastService.allColors();
  }

  loadTags(): void {
    if (this.tags.length === 0) {
      this.tagsProxyService.getTags().subscribe({
        next: (response) => {
          this.tags = response.data ?? [];
        },
      });
    }
  }

  getFabricLongDescription(fabricCode: string) {
    return this.allPCFabrics.find((facric) => facric.fabricCode === fabricCode)?.longDescription;
  }

  getTotalLoopLengthByFabricCode(fabricCode: string): number[] {
    const loopLength: number[] = [];
    const fabric = this.allPCFabrics.find((f) => f.fabricCode === fabricCode);
    if (!fabric) return [0];

    fabric.greige.yarnDetails.forEach((yarn) => {
      loopLength.push(yarn.loopLength!);
    });

    return loopLength;
  }

  getAllLoopLength() {
    this.allPCFabrics.forEach((fabric) => {
      fabric.greige.yarnDetails.forEach((yarn) => {
        if (!this.filterLoopLength.find((l) => l === yarn.loopLength)) {
          this.filterLoopLength.push(yarn.loopLength!);
        }
      });
    });
    // sort from minor to mayor
    this.filterLoopLength = this.filterLoopLength.sort(function (a, b) {
      return a - b;
    });
  }

  get filterLoopLengthOptions() {
    return this.filterLoopLength.map((value) => ({
      label: value.toString(),
      value: value,
    }));
  }

  verifyIfProductionCardIsSelected(card: FormGroup<ProductionCardFormGroup>): boolean {
    if (!this.currentProductionCards) return false;
    return (
      this.currentProductionCards.controls.some((ctrl) => ctrl.get('id')?.value === card.get('id')?.value) ||
      this.productionCardsSelected.controls.some((ctrl) => ctrl.get('id')?.value === card.get('id')?.value)
    );
  }

  public onProductionCardSelected(selected: boolean, productionCardFormGroup: FormGroup<ProductionCardFormGroup>) {
    if (!this.canEditProductionCard(productionCardFormGroup)) {
      console.warn('Production card cannot be selected in current status:', productionCardFormGroup.get('status')?.value);
      return;
    }

    if (selected) {
      this.productionCardSelected.emit(productionCardFormGroup);
    } else {
      this.productionCardUnselected.emit(productionCardFormGroup);
    }
  }

  canEditProductionCard(productionCardFormGroup: FormGroup<ProductionCardFormGroup>): boolean {
    const status = productionCardFormGroup.get('status')?.value;
    return ProductionCardStatusUtil.CanEdit(status!);
  }

  isProductionCardReadonly(productionCardFormGroup: FormGroup<ProductionCardFormGroup>): boolean {
    if (this.readonly) return true;
    return !this.canEditProductionCard(productionCardFormGroup);
  }
}
